import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { PageTitle } from '../../../_metronic/layout/core'
import { getErrorCode } from '../../common/api/errors'
import RefusalFacts from './components/RefusalFacts'
import TimelinePointItem from './components/TimelinePointItem'
import { BORDER, INK, MUTED, NO_BREADCRUMBS, flowControlRefusalCardUrl } from './core/consts'
import { getPayoutRefusalCard } from './core/refusalApi'
import { PayoutRefusalCard } from './core/refusal.types'

/**
 * A payout init the platform refused: there is no ticket, so the card is addressed by the log row
 * and shows the request, the merchant's answer and why — on the shell of the ticket card.
 */
const PayoutRefusalCardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [card, setCard] = useState<PayoutRefusalCard | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return

    let cancelled = false
    setCard(null)
    setError(null)

    getPayoutRefusalCard(id)
      .then(({ data }) => {
        if (cancelled) return
        if (!data.success) {
          setError(data.errors.general_error ?? 'Не удалось загрузить отказ')
          return
        }
        setCard(data.data)
      })
      .catch((e) => {
        if (cancelled) return
        setError(getErrorCode(e) === 'PAYOUT_REFUSAL_NOT_FOUND' ? 'Отказ не найден' : 'Не удалось загрузить отказ')
      })

    return () => {
      cancelled = true
    }
  }, [id])

  const matchingRefusalId = card?.links.matchingRefusalId ?? null

  return (
    <>
      <PageTitle breadcrumbs={NO_BREADCRUMBS}>Отказ в создании выплаты</PageTitle>

      {error && (
        <div className='card' style={{ padding: '24px 28px' }}>
          <div className='text-danger' style={{ fontSize: 14 }}>
            {error}
          </div>
        </div>
      )}

      {!error && !card && (
        <div className='d-flex justify-content-center' style={{ padding: 48 }}>
          <Spinner animation='border' variant='primary' />
        </div>
      )}

      {!error && card && (
        <>
          <div className='d-flex align-items-center' style={{ gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: INK }}>Отказ в создании выплаты</h1>
            <span className='badge badge-light-danger'>{card.header.statusTitle}</span>
            <span style={{ fontSize: 13, color: MUTED }}>{card.header.reasonTitle}</span>
            <div style={{ flexGrow: 1 }} />
            {matchingRefusalId !== null && (
              <Link to={flowControlRefusalCardUrl(matchingRefusalId)} className='btn btn-sm btn-light-primary'>
                Решение мэтчинга
              </Link>
            )}
          </div>

          <div className='d-flex' style={{ gap: 18, alignItems: 'flex-start' }}>
            <div style={{ width: 396, flexShrink: 0 }}>
              <RefusalFacts title='Данные запроса' facts={card.facts} client={card.client} />
            </div>

            <div className='card' style={{ flexGrow: 1, minWidth: 0 }}>
              <div
                className='d-flex align-items-center'
                style={{ minHeight: 56, padding: '0 28px', gap: 10, borderBottom: `1px solid ${BORDER}` }}
              >
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>История</h2>
                <span style={{ fontSize: 12.5, color: MUTED }}>{card.timeline.length} событий</span>
              </div>

              {!card.journalAvailable && (
                <div className='alert alert-warning' style={{ margin: '18px 28px 0', fontSize: 13 }}>
                  Журнал интеграций не ответил — запрос мерчанта в этой ленте не показан.
                </div>
              )}

              <div className='d-flex flex-column' style={{ padding: '22px 28px' }}>
                {card.timeline.map((point, index) => (
                  <TimelinePointItem
                    key={`${point.at}-${point.kind}-${index}`}
                    point={point}
                    last={index === card.timeline.length - 1}
                  />
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default PayoutRefusalCardPage
